import type { EvaluationBlock } from '@interactive-room';

export type PrintBlockRow =
  | { kind: 'single'; block: EvaluationBlock; index: number }
  | { kind: 'pair'; blocks: [EvaluationBlock, EvaluationBlock]; indices: [number, number] };

const COMPACT_PAIR_MODES = new Set<string>([
  'completar',
  'arena',
  'destacado',
  'vincula',
  'agrupa',
  'order',
]);

function payloadRecord(payload: unknown): Record<string, unknown> {
  return payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
}

function estimateBlockWeight(block: EvaluationBlock): number {
  const payload = payloadRecord(block.payload);
  const mode = block.mode;

  if (mode === 'subtitulo' || mode === 'info_block' || mode === 'figura' || mode === 'glosario_tabla') {
    return 999;
  }

  if (mode === 'formula_destacada' || mode === 'geometry_canvas' || mode === 'cartesian_plane' || mode === 'math_equation' || mode === 'number_line' || mode === 'counting_collection' || mode === 'data_chart' || mode === 'fraction_model' || mode === 'percent_grid' || mode === 'canvas_breakdown') {
    return 999;
  }

  if (mode === 'completar') {
    return String(payload.text || '').length;
  }

  if (mode === 'destacado') {
    return String(payload.body || '').length;
  }

  if (mode === 'arena') {
    const questions = Array.isArray(payload.questions) ? payload.questions : [];
    return questions.reduce(
      (sum, q) => sum + String((q as { prompt?: string }).prompt || '').length,
      questions.length * 40,
    );
  }

  if (mode === 'vincula') {
    const pairs = Array.isArray(payload.pairs) ? payload.pairs : [];
    return pairs.length * 35;
  }

  if (mode === 'agrupa') {
    const items = Array.isArray(payload.items) ? payload.items : [];
    return items.length * 30;
  }

  if (mode === 'order') {
    const items = Array.isArray(payload.items) ? payload.items : [];
    return items.length * 25;
  }

  return 500;
}

export function isCompactPrintBlock(block: EvaluationBlock): boolean {
  if (!COMPACT_PAIR_MODES.has(block.mode)) return false;
  return estimateBlockWeight(block) <= 320;
}

export function groupPrintBlockRows(blocks: EvaluationBlock[]): PrintBlockRow[] {
  const rows: PrintBlockRow[] = [];
  let i = 0;

  while (i < blocks.length) {
    const current = blocks[i]!;
    const next = blocks[i + 1];

    if (
      next &&
      isCompactPrintBlock(current) &&
      isCompactPrintBlock(next) &&
      !payloadRecord(current.payload).stimulus &&
      !payloadRecord(next.payload).stimulus
    ) {
      rows.push({
        kind: 'pair',
        blocks: [current, next],
        indices: [i, i + 1],
      });
      i += 2;
      continue;
    }

    rows.push({ kind: 'single', block: current, index: i });
    i += 1;
  }

  return rows;
}
